import React, { useMemo } from 'react';

import constants from 'constants/constants';
import { orderBy } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { useBasePath } from 'hooks/useBasePath';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { IContract, IProject, useProjectTreeStore } from 'store/projectContract.store';

import { ICreateProjectPayload, useMutationCreateProject } from 'api/mutations/createProject.mutation';
import { IAllUserGroups, useQueryGetAllUserGroups } from 'api/queries/getAllUserGroups.query';

import { ProjectTreeNode, findeNodeByUUID } from 'components/common/TreeProgramFilter/ProjectTree/projectTree.utils';

import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Select } from 'components/DesignSystem/Select';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

import classes from './AddProjectModal.module.scss';

const groupsMapper = (item: string) => ({ value: item, label: item });
const groupsMapperBe = (item: IAllUserGroups) => ({
    value: item.group_name,
    label: item.group_name,
});

interface IAddProjectModalForm {
    initialContractName: string;
    projectName: string;
    userGroups: ISelectOption<string>[];
}

interface IAddProjectModal {
    showModal: boolean;
    onHideModal: () => void;
    callback?: (project: IProject | null, contract: IContract | null) => void;
}

export const AddProjectModal = ({ showModal, onHideModal, callback }: IAddProjectModal) => {
    const history = useHistory();
    const basePath = useBasePath();

    const { ability, userGroupList } = useUserHook();
    const { customerId } = useUserHook();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const { mutate: mutationCreateProject, isLoading: isLoadingCreateProject } = useMutationCreateProject();
    const { update } = useProjectTreeStore();
    const { data: managementUserGroups } = useQueryGetAllUserGroups({
        enabled: ability.can('view', 'UserManagement'),
        isProjectCreation: true,
    });
    const { sendEventWithDimensions } = useEventWithDimensions();
    const {
        handleSubmit,
        setValue,
        control,
        formState: { errors },
        reset,
    } = useForm<IAddProjectModalForm>({
        defaultValues: {
            projectName: '',
            initialContractName: '',
            userGroups: [],
        },
    });

    const handleSaveUseForm = (value) => {
        const label: string[] = [];
        label.push(`Project: ${value.projectName}`);
        label.push(`Sub-program: ${value.initialContractName === '' ? 'default' : value.initialContractName}`);
        label.push(`User Groups: ${value.userGroups.join(', ')}`);
        sendEventWithDimensions({
            category: 'Project/Sub-program',
            action: 'Create',
            label: label.join('; '),
        });
        handleSaveProject({ ...value, userGroups: value.userGroups.map((item) => item.label) });
        closeModal();
    };

    /**
     * get userGroups from management only if user has Admin or UserManagment in his privilegeMap property
     * if not - get the list from current user userGroupList property
     * */
    const userGroupsOptions = useMemo<ISelectOption<string>[]>(() => {
        const options = managementUserGroups?.map(groupsMapperBe) || userGroupList?.map(groupsMapper) || [];
        return orderBy(options, [(item: any) => item['label'].toLowerCase()], ['asc']);
    }, [managementUserGroups]);

    const closeModal = () => {
        reset();
        onHideModal();
    };

    const handleSaveProjectCallback = ({
        projectUid,
        newData,
    }: {
        projectUid: ProjectTreeNode['uuid'];
        newData?: ProjectTreeNode;
    }) => {
        const projectNode = newData && projectUid ? findeNodeByUUID(newData, projectUid) : null;
        const contractNode = projectNode && projectNode.children ? projectNode.children[0] : null;
        const selectedContract = contractNode
            ? {
                  id: contractNode.id,
                  title: contractNode.name,
                  uuid: contractNode.uuid,
              }
            : null;

        if (projectNode) {
            update({
                project: {
                    id: projectNode.id,
                    title: projectNode.name,
                    uuid: projectNode.uuid,
                },
                contract: selectedContract,
            });
            if (callback) {
                callback(
                    {
                        id: projectNode.id,
                        title: projectNode.name,
                        uuid: projectNode.uuid,
                    },
                    contractNode
                        ? {
                              id: contractNode.id,
                              title: contractNode.name,
                              uuid: contractNode.uuid,
                          }
                        : null,
                );
            }
        }

        update({ parentNode: null });
    };

    const handleSaveProject = (value: Omit<ICreateProjectPayload, 'customerId'>) => {
        mutationCreateProject(
            { ...value, customerId },
            {
                onSuccess: (response) => {
                    onHideModal();
                    queryClient.invalidateQueries({ queryKey: 'getGeneralHierarchyList' }).then(() => {
                        const newData = queryClient.getQueryData<ProjectTreeNode>('getGeneralHierarchyList');
                        handleSaveProjectCallback({ projectUid: response.projectUid, newData });
                        handleSnackBar('Project created successfully', 'success');

                        // should return to base url to show the data for the new project
                        // not for program Library because we need to add the new project and contract ID's to the url
                        if (
                            basePath !==
                            `/${AppRoutes.DASHBOARD}/${AppRoutes.PLANNING}/${executionConfig.programLibrary.link}`
                        ) {
                            history.push(basePath);
                        }
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    return (
        <Modal
            open={showModal}
            onClose={closeModal}
            title={'Add Project'}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        isLoading={isLoadingCreateProject}
                        disabled={isLoadingCreateProject}
                        onClick={handleSubmit(handleSaveUseForm)}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <div className={classes.addProjectModalContainer}>
                <form onSubmit={handleSubmit(handleSaveUseForm)} autoComplete={'off'}>
                    <div className={classes.inputTitle}>Name *</div>
                    <div className={classes.inputWrapper}>
                        <Controller
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    placeholder={'Project name...'}
                                    maxLength={constants.MAX_PROJECT_NAME_LENGTH}
                                    onClear={() => {
                                        setValue('projectName', '');
                                    }}
                                />
                            )}
                            name={`projectName`}
                            control={control}
                            rules={{
                                required: 'Project Name is Required',
                                minLength: {
                                    value: 2,
                                    message: 'Project Name is too short',
                                },
                                pattern: {
                                    value: /^(?!\s+$).*/,
                                    message: 'Name cannot be empty',
                                },
                            }}
                        />
                    </div>

                    {errors.projectName && <CustomizedFormErrorMessage text={errors.projectName.message} />}
                    <div className={classes.inputTitle}>Initial Sub-Program name</div>

                    <div className={classes.inputWrapper}>
                        <Controller
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    placeholder={'Project initial Sub-Program name...'}
                                    maxLength={constants.MAX_SUB_PROGRAM_NAME_LENGTH}
                                    onClear={() => {
                                        setValue('initialContractName', '');
                                    }}
                                />
                            )}
                            name={`initialContractName`}
                            control={control}
                            rules={{
                                minLength: {
                                    value: 2,
                                    message: 'Initial Sub-Program Name is too short',
                                },
                            }}
                        />
                    </div>

                    {errors.initialContractName && (
                        <CustomizedFormErrorMessage text={errors.initialContractName.message} />
                    )}
                    <div className={classes.inputTitle}>Select who can access this project (User Groups)</div>

                    <div className={classes.inputWrapper}>
                        <Controller
                            render={({ field }) => (
                                <Select<ISelectOption<string>, true>
                                    {...field}
                                    options={userGroupsOptions}
                                    isClearable={true}
                                    isMulti
                                />
                            )}
                            name={'userGroups'}
                            control={control}
                            rules={{
                                validate: {
                                    required: (value) => value.length > 0 || 'Must select at least one user group',
                                },
                            }}
                        />
                    </div>

                    {errors.userGroups && <CustomizedFormErrorMessage text={errors.userGroups.message} />}
                </form>
            </div>
        </Modal>
    );
};
